<template>
  <div>
    <VCard no-border style="max-width: 1200px" type="neutral">
      <VTable :cell-padding="2" :data="[info]" no-border>
        <VTableColumn :label="$t('name')" prop="name" />
        <VTableColumn :label="$t('file-size')" prop="size" />
        <VTableColumn :label="$t('result')" prop="result" />
        <VTableColumn :cell-class="invalidCount > 0 ? 'text-error-500' : 'text-success-500'" :label="$t('validation')" prop="validation" />
        <VTableColumn>
          <VButton size="small" style="margin-left: 50px" type="error" @click="file = undefined">{{ $t('remove-file') }}</VButton>
          <VButton size="small" type="secondary" @click="downloadFile">{{ $t('download-file') }}</VButton>
        </VTableColumn>
      </VTable>
    </VCard>

    <VTable :cell-padding="4" class="preview-table" :data="rows" editable-cells :pagination="{}" row-key="index">
      <VTableColumn v-for="(label, prop) in visibleFields" :key="prop" v-slot="{ row }" cell-class="flex-center" :label :prop>
        <VIconHelp v-if="showErrorIcon(row, prop)" :help="[$t('invalid')]" icon="Info" :size="14" style="margin-right: 5px; height: 14px" type="error" />

        <SelectRoles
          v-if="prop === 'roles'"
          clearable
          :model-value="rows[row.index].roles.filter((role) => !isAppModuleRole(role))"
          :placeholder="$t('roles')"
          @update:model-value="onInput($event, 'roles', row.index)"
        />

        <SelectModuleRoles
          v-if="prop === 'module_roles'"
          :model-value="rows[row.index].roles.filter(isAppModuleRole)"
          :placeholder="$t('modules')"
          @update:model-value="onInput($event, 'module_roles', row.index)"
        />

        <VInput
          v-else-if="prop === 'email' || prop === 'first_name' || prop === 'last_name'"
          v-model="rows[row.index][prop]"
          minimal
          size="small"
          @update:model-value="onInput($event, prop, row.index)"
        />
      </VTableColumn>
      <VTableColumn v-slot="{ row }" cell-class="flex-center">
        <VButton v-if="row.index !== lastRow?.index" icon="Bin" type="link" @click.stop="removeRow(row.index)" />
      </VTableColumn>
    </VTable>
  </div>
</template>

<script setup lang="ts">
import { RequestError } from '@vartion/ui'
import download from 'downloadjs'
import { debounce } from 'lodash-es'
// eslint-disable-next-line import-x/default
import Papa from 'papaparse'

import { APP_MODULE_ROLES, APP_ROLES, isAppModuleRole } from '~/stores/roles.ts'

const invalidFileFormat = () => $t('this-file-format-is-not-supported-please-use-a-csv-or-json-file-with-utf-8-encoding')

const emptyRow = () => ({ first_name: '', last_name: '', email: '', roles: [] })

export interface UserRow {
  first_name: string
  last_name: string
  email: string
  roles: string[]
  index?: number
}

interface CSVUserRow {
  first_name: string
  last_name: string
  email: string
  [key: string]: string
}

const file = defineModel<File | undefined>('file', { default: undefined })
const valid = defineModel<boolean>('valid', { required: true })
const rows = defineModel<UserRow[]>('rows', { required: true })

const content = ref('')
const invalidEmails = ref(new Set<string>())
const fields = {
  first_name: $t('first-name'),
  last_name: $t('last-name'),
  email: $t('email'),
  roles: $t('roles'),
}

const validateEmails = debounce(originalValidateEmails, 300)

const totalCount = computed(() => rows.value.length - 1)
const invalidCount = computed(
  () => rows.value.slice(0, -1).filter((row) => !row.first_name || !row.last_name || !row.email || invalidEmails.value.has(row.email) || rolesAreInvalid(row.roles)).length,
)
const size = computed(() => humanSize(file.value?.size ?? 0))
const isCSV = computed(() => file.value?.name.toLowerCase().endsWith('.csv'))
const lastRow = computed(() => rows.value.at(-1))

const info = computed(() => ({
  result: $t('count-rows', totalCount.value),
  size: size.value,
  validation: invalidCount.value > 0 ? $t('count-invalid', invalidCount.value) : $t('valid'),
  name: file.value?.name,
}))

const visibleFields = computed(() => {
  const visibleFields: Record<string, string> = {}

  for (const [key, label] of Object.entries(fields)) {
    if (key === 'roles') {
      if (!can('userRoles.update')) {
        continue
      }

      visibleFields.roles = label
      visibleFields.module_roles = $t('modules')
      continue
    }

    visibleFields[key as keyof UserRow] = label
  }

  return visibleFields
})

watch(
  lastRow,
  (row) => {
    if (!row) {
      return
    }

    if (row.first_name || row.last_name || row.email || row.roles?.length) {
      rows.value.push({ ...emptyRow(), index: rows.value.length })
    }
  },
  { deep: true },
)

const reader = new FileReader()
reader.onload = (event) => {
  if (event.target?.readyState !== 2) {
    return
  }

  if (event.target.error) {
    return
  }

  if (typeof event.target.result === 'string') {
    content.value = event.target.result
    createRows()
    validateEmails()
  }
}

onBeforeMount(() => {
  if (file.value) {
    reader.readAsText(file.value)
  }
})

function updateValid() {
  valid.value = totalCount.value > 0 && invalidCount.value === 0
}

function createRows() {
  rows.value = [...parse(), emptyRow()].map((row, index) => ({ ...row, index }))
}

function rolesAreInvalid(roles: string[]): boolean {
  return roles.filter((role) => !APP_MODULE_ROLES.has(role)).some((item) => !APP_ROLES.has(item))
}

function parse(): UserRow[] {
  if (content.value === '') {
    return []
  }

  if (isCSV.value) {
    if (content.value.startsWith('first_name')) {
      const result = Papa.parse<CSVUserRow>(content.value, { header: true, skipEmptyLines: true })

      return result.data.map((row) => {
        const formatted: UserRow = {
          first_name: row.first_name,
          last_name: row.last_name,
          email: row.email,
          roles: [],
        }

        if (!can('userRoles.update')) {
          return formatted
        }

        let rowKey: keyof typeof row
        for (rowKey in row) {
          if (rowKey.startsWith('roles') && row[rowKey]) {
            formatted.roles.push(row[rowKey] as string)
          }
        }

        return formatted
      })
    }

    $message.error(invalidFileFormat())
  } else {
    let rows: UserRow[] = []

    try {
      rows = JSON.parse(content.value)
    } catch {
      $message.error(invalidFileFormat())
    }

    for (const row of rows) {
      row.email = row.email.toLowerCase()
      row.roles ??= []

      if (!can('userRoles.update')) {
        row.roles = []
      }
    }

    return rows
  }

  return []
}

async function originalValidateEmails() {
  const emails = rows.value.map((row) => row.email).filter((email) => email && !invalidEmails.value.has(email))

  if (emails.length === 0) {
    updateValid()

    return
  }

  try {
    await api.post('validate-emails', { emails })
  } catch (error) {
    if (error instanceof RequestError && error.response?.status === 422) {
      for (const email of Object.keys(error.response.data.errors).map((error) => emails[Number(error.split('.').pop() as string)]) as string[]) {
        invalidEmails.value.add(email)
      }
    } else {
      throw error
    }
  }

  updateValid()
}

async function onInput(value: string[] | string | number, prop: string, index: number) {
  if (prop === 'email' && typeof value === 'string') {
    valid.value = false
    rows.value[index][prop] = value.toLowerCase()
    await validateEmails()
  } else if (prop === 'roles' && Array.isArray(value)) {
    rows.value[index][prop] = [...value, ...rows.value[index][prop].filter((role) => APP_MODULE_ROLES.has(role))]
  } else if (prop === 'module_roles' && Array.isArray(value)) {
    rows.value[index].roles = [...value, ...rows.value[index].roles.filter((role) => !APP_MODULE_ROLES.has(role))]
  } else {
    updateValid()
  }

  checkEmptyRows()
}

function checkEmptyRows() {
  const secondToLastRow = rows.value.at(-2)
  if (secondToLastRow && !secondToLastRow?.first_name && !secondToLastRow.last_name && !secondToLastRow.email && !secondToLastRow.roles.length) {
    rows.value.pop()
    checkEmptyRows()
  }
  updateValid()
}

function removeRow(index: number) {
  const arrayIndex = rows.value.findIndex((row) => row.index === index)
  if (arrayIndex !== -1) {
    rows.value.splice(arrayIndex, 1)
    rows.value.forEach((row, newIndex) => {
      row.index = newIndex
    })
  }

  updateValid()
}

function downloadFile() {
  let data: string
  if (isCSV.value) {
    data = Papa.unparse(rows.value, { columns: ['first_name', 'last_name', 'email'] })
  } else {
    data = JSON.stringify(rows.value)
  }

  download(data, file.value?.name, isCSV.value ? 'text/csv' : 'application/json')
}

function showErrorIcon(row: UserRow, prop: string): boolean {
  if (row.index === lastRow.value?.index) {
    return false
  }

  if (prop === 'email') {
    return !row.email || invalidEmails.value.has(row.email)
  }

  if (prop === 'roles') {
    return rolesAreInvalid(row.roles)
  }

  return prop !== 'module_roles' && !row[prop as keyof UserRow]
}
</script>
